/**
 * Build a reverse lookup map from a given ADF map.
 * @param adfMap - Record of ADF bucket to list of fields.
 */

import _ from 'lodash';

export const CUSTOM_ATTRIBUTE_PREFIX = 'CA:';

export const filterDataKeys = {
	claimId: 'claimId',
	claimAge: 'claimAge',
	claimType: 'claimType',
	claimNumber: 'claimNumber',
	claimAmount: 'claimAmount',
	claimStatus: 'claimStatus',
	frequencyType: 'frequencyType',

	claimResponseId: 'claimResponseId',
	claimResponseIds: 'claimResponseIds',

	patientId: 'patientId',
	patientName: 'patientName',
	patientMRN: 'patientMRN',
	patientSSN: 'patientSSN',
	patientIdentifier: 'patientIdentifier',
	patientResponsibility: 'patientResponsibility',

	serviceDate: 'serviceDate',
	inquiryDate: 'inquiryDate',
	pendingPR: 'pendingPR',

	payerId: 'payerId',
	payerName: 'payerName',
	payerResponsibilitySequence: 'payerResponsibilitySequence',

	providerType: 'providerType',
	providerId: 'providerId',

	workState: 'workState',
	worklistId: 'worklistId',

	accountId: 'accountId',
	accountAge: 'accountAge',
	accountNumber: 'accountNumber',

	guarantorName: 'guarantorName',
	guarantor: 'guarantor',
	guarantorId: 'guarantorId',
	guarantorType: 'guarantorType',

	batchId: 'batchId',
	batchPostPaymentId: 'batchPostPaymentId',
	batchPostPaymentStatus: 'batchPostPaymentStatus',

	paymentReconciliationId: 'paymentReconciliationId',
	postPaymentSelected: 'postPaymentSelected',

	eligibilityId: 'eligibilityNumber',
	id: 'id',
	locationId: 'locationId',
	dateOfService: 'dateOfService',
	coverageStatus: 'coverageStatus',
	status: 'status',
	createdBy: 'createdBy',
	creationDate: 'creationDate',
	completionTime: 'completionTime',
	requestType: 'requestType',
	transactionType: 'transactionType',
	payer: 'payer',
	billedAmount: 'billedAmount',
	accountReceivable: 'accountReceivable',
	outcome: 'outcome',
	source: 'source',
	referenceNumber: 'referenceNumber',
	eraNumber: 'eraNumber',
	created: 'created',
	updated: 'updated',
	requestId: 'requestId',
	coverageId: 'coverageId',
	method: 'method',
	date: 'date',
	amount: 'amount',
	name: 'name',
	birthDate: 'birthDate',
	MRN: 'MRN',
	SSN: 'SSN',
	memberId: 'memberId',
	transactions: 'transactions',
	enrollmentRequired: 'enrollmentRequired',
	npi: 'npi',
	type: 'type',
	priority: 'priority',
	assignees: 'assignees',
	encounterId: 'encounterId',
	providerName: 'providerName',
	encounterServiceDate: 'encounterServiceDate',
	encounterProviderName: 'encounterProviderName',
	encounterNumber: 'encounterNumber',
};

export const CRITERIA_KEYS = {
	criteria: 'criteria',
	patientADF: 'patientADF',
	guarantorADF: 'guarantorADF',
	providerADF: 'providerADF',
	worklistADF: 'worklistADF',
	claimresponseADF: 'claimresponseADF',
	paymentreconciliationADF: 'paymentreconciliationADF',
	batchpostpaymentADF: 'batchpostpaymentADF',
	payerADF: 'payerADF',
	claimADF: 'claimADF',
	accountADF: 'accountADF',
	customAttributeADF: 'customAttributeADF',
	encounterADF: 'encounterADF',
} as const;
export type CriteriaKey = keyof typeof CRITERIA_KEYS;

/**
 * Mapping of account advanced filter fields to their ADF buckets.
 * Keys are the ADF group names expected by the backend.
 */
export const getAccountAdfFieldMap = (
	customAttributes: CustomAttributes.IAllResponse[],
): Partial<Record<keyof Accounts.IAccountSearchCriteria, string[]>> => {
	const customAttributeKeys = _.map(customAttributes, (attribute) => `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`);
	return {
		[CRITERIA_KEYS.criteria]: [
			filterDataKeys.accountAge,
			filterDataKeys.accountNumber,
			filterDataKeys.billedAmount,
			filterDataKeys.pendingPR,
			filterDataKeys.accountId,
			filterDataKeys.accountReceivable,
		],
		[CRITERIA_KEYS.patientADF]: [
			filterDataKeys.patientId,
			filterDataKeys.patientName,
			filterDataKeys.patientMRN,
			filterDataKeys.patientSSN,
			filterDataKeys.patientIdentifier,
		],
		[CRITERIA_KEYS.guarantorADF]: [filterDataKeys.guarantorName, filterDataKeys.guarantorId],
		...(!_.isEmpty(customAttributeKeys) ? { [CRITERIA_KEYS.customAttributeADF]: customAttributeKeys } : {}),
	};
};

export const BATCH_HISTORY_ADF_FIELD_MAP: Partial<Record<keyof EligibilityBatchUpload.IBatchEligibilityHistoryQueryModel, string[]>> = {
	[CRITERIA_KEYS.criteria]: [
		filterDataKeys.batchId,
		filterDataKeys.status,
		filterDataKeys.createdBy,
		filterDataKeys.creationDate,
		filterDataKeys.completionTime,
		filterDataKeys.requestType,
		filterDataKeys.payer,
	],
};

export const BATCH_POST_PAYMENT_ADF_FIELD_MAP: Partial<Record<keyof PatientPayment.IBatchPostPaymentSearchParams, string[]>> = {
	[CRITERIA_KEYS.criteria]: [
		filterDataKeys.batchId,
		filterDataKeys.status,
		filterDataKeys.requestType,
		filterDataKeys.transactionType,
		filterDataKeys.createdBy,
		filterDataKeys.creationDate,
		filterDataKeys.completionTime,
	],
};

// useValidateBatchEligibilityResubmission
export const BATCH_RESUBMIT_ADF_FIELD_MAP: Partial<Record<keyof EligibilityRequests.IBatchEligibilityResubmitQueryModel, string[]>> = {
	[CRITERIA_KEYS.criteria]: [
		filterDataKeys.id,
		filterDataKeys.providerId,
		filterDataKeys.payerId,
		filterDataKeys.inquiryDate,
		filterDataKeys.patientName,
		filterDataKeys.patientIdentifier,
		filterDataKeys.locationId,
		filterDataKeys.dateOfService,
		filterDataKeys.batchId,
		filterDataKeys.coverageStatus,
	],
};

export const getClaimAdfFieldMap = (
	customAttributes: CustomAttributes.IAllResponse[],
): Partial<Record<keyof ClaimStatusRequests.IClaimSearchCriteria, string[]>> => {
	const customAttributeKeys = _.map(customAttributes, (attribute) => `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`);
	return {
		[CRITERIA_KEYS.criteria]: [
			filterDataKeys.claimId,
			filterDataKeys.claimAge,
			filterDataKeys.claimType,
			filterDataKeys.claimNumber,
			filterDataKeys.claimAmount,
			filterDataKeys.claimStatus,
			filterDataKeys.frequencyType,
			filterDataKeys.serviceDate,
			filterDataKeys.inquiryDate,
			filterDataKeys.pendingPR,
			filterDataKeys.patientResponsibility,
			filterDataKeys.payerResponsibilitySequence,
			filterDataKeys.workState,
			filterDataKeys.payerId,
		],
		[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountNumber, filterDataKeys.accountId],
		[CRITERIA_KEYS.patientADF]: [
			filterDataKeys.patientId,
			filterDataKeys.patientName,
			filterDataKeys.patientIdentifier,
			filterDataKeys.birthDate,
		],
		[CRITERIA_KEYS.guarantorADF]: [filterDataKeys.guarantorName, filterDataKeys.guarantorId],
		[CRITERIA_KEYS.providerADF]: [filterDataKeys.providerId],
		[CRITERIA_KEYS.worklistADF]: [filterDataKeys.worklistId],
		[CRITERIA_KEYS.claimresponseADF]: [
			filterDataKeys.claimResponseId,
			filterDataKeys.claimResponseIds,
			filterDataKeys.status,
			filterDataKeys.outcome,
			filterDataKeys.postPaymentSelected,
			filterDataKeys.source,
		],
		[CRITERIA_KEYS.paymentreconciliationADF]: [
			filterDataKeys.paymentReconciliationId,
			filterDataKeys.referenceNumber,
			filterDataKeys.eraNumber,
		],
		[CRITERIA_KEYS.batchpostpaymentADF]: [filterDataKeys.batchPostPaymentId, filterDataKeys.batchPostPaymentStatus],
		[CRITERIA_KEYS.payerADF]: [
			filterDataKeys.name,
			filterDataKeys.status,
			filterDataKeys.transactions,
			filterDataKeys.enrollmentRequired,
		],
		...(!_.isEmpty(customAttributeKeys) ? { [CRITERIA_KEYS.customAttributeADF]: customAttributeKeys } : {}),
	};
};

// Not used currently, but is defined in the backend
// export const CLAIM_RESPONSE_ADF_FIELD_MAP: Record<CriteriaKey, string[]> = {
// 	[CRITERIA_KEYS.criteria]: [
// 		filterDataKeys.claimResponseId,
// 		filterDataKeys.claimId,
// 		filterDataKeys.source,
// 		filterDataKeys.postPaymentSelected,
// 		filterDataKeys.outcome,
// 		filterDataKeys.status,
// 		filterDataKeys.created,
// 		filterDataKeys.updated,
// 		filterDataKeys.patientId,
// 		filterDataKeys.payerId,
// 	],
// };

export const ELIGIBILITY_ADF_FIELD_MAP: Partial<Record<keyof EligibilityRequests.IEligibilityRequestSearchCriteria, string[]>> = {
	[CRITERIA_KEYS.criteria]: [
		filterDataKeys.id,
		filterDataKeys.patientId,
		filterDataKeys.providerId,
		filterDataKeys.coverageStatus,
		filterDataKeys.patientName,
		filterDataKeys.patientIdentifier,
		filterDataKeys.payerId,
		filterDataKeys.inquiryDate,
		filterDataKeys.dateOfService,
		filterDataKeys.requestId,
		filterDataKeys.batchId,
		filterDataKeys.locationId,
		filterDataKeys.coverageId,
		filterDataKeys.eligibilityId,
	],
};

export const getEraAdfFieldMap = (
	customAttributes: CustomAttributes.IAllResponse[],
): Partial<Record<keyof ERA.IERASearchCriteria, string[]>> => {
	const customAttributeKeys = _.map(customAttributes, (attribute) => `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`);
	return {
		[CRITERIA_KEYS.criteria]: [
			filterDataKeys.eraNumber,
			filterDataKeys.method,
			filterDataKeys.referenceNumber,
			filterDataKeys.amount,
			filterDataKeys.date,
			filterDataKeys.outcome,
			filterDataKeys.status,
			filterDataKeys.workState,
		],
		[CRITERIA_KEYS.claimADF]: [filterDataKeys.claimId, filterDataKeys.claimNumber],
		[CRITERIA_KEYS.payerADF]: [filterDataKeys.payerName],
		...(!_.isEmpty(customAttributeKeys) ? { [CRITERIA_KEYS.customAttributeADF]: customAttributeKeys } : {}),
	};
};

export const GUARANTOR_ADF_FIELD_MAP: Partial<Record<keyof Guarantor.GuarantorSearch, string[]>> = {
	[CRITERIA_KEYS.criteria]: [filterDataKeys.guarantorId, filterDataKeys.guarantorType, filterDataKeys.guarantorName],
	[CRITERIA_KEYS.patientADF]: [filterDataKeys.patientName, filterDataKeys.patientId, filterDataKeys.patientMRN],
	[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountNumber, filterDataKeys.accountId],
};
export const LOCATION_ADF_FIELD_MAP: Partial<Record<keyof Location.IServicelocationSearchCriteria, string[]>> = {
	[CRITERIA_KEYS.criteria]: [filterDataKeys.id, filterDataKeys.name],
};

export const PATIENT_ADF_FIELD_MAP: Partial<Record<keyof Patients.IPatientSearchCriteria, string[]>> = {
	[CRITERIA_KEYS.criteria]: [
		filterDataKeys.patientName,
		filterDataKeys.birthDate,
		filterDataKeys.MRN,
		filterDataKeys.SSN,
		filterDataKeys.patientResponsibility,
		filterDataKeys.memberId,
	],
	[CRITERIA_KEYS.guarantorADF]: [filterDataKeys.guarantorId, filterDataKeys.guarantorName],
	[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountNumber, filterDataKeys.accountId],
};

// Not used currently, but is defined in the backend
// export const PAYER_ADF_FIELD_MAP: Record<CriteriaKey, string[]> = {
// 	[CRITERIA_KEYS.criteria]: [filterDataKeys.name, filterDataKeys.status, filterDataKeys.transactions, filterDataKeys.enrollmentRequired],
// };

export const getPaymentReconciliationAdfFieldMap = (
	customAttributes: CustomAttributes.IAllResponse[],
): Partial<Record<keyof PatientPayment.IPaymentReconciliationSearchCriteria, string[]>> => {
	const customAttributeKeys = _.map(customAttributes, (attribute) => `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`);
	return {
		[CRITERIA_KEYS.criteria]: [
			filterDataKeys.referenceNumber,
			filterDataKeys.date,
			filterDataKeys.outcome,
			filterDataKeys.status,
			filterDataKeys.eraNumber,
			filterDataKeys.amount,
		],
		[CRITERIA_KEYS.claimADF]: [filterDataKeys.claimId, filterDataKeys.claimNumber],
		[CRITERIA_KEYS.guarantorADF]: [filterDataKeys.guarantorId, filterDataKeys.guarantorName],
		...(!_.isEmpty(customAttributes) ? { [CRITERIA_KEYS.customAttributeADF]: customAttributeKeys } : {}),
	};
};

export const PROVIDER_ADF_FIELD_MAP: Partial<Record<keyof Provider.IProviderSearchCriteria, string[]>> = {
	[CRITERIA_KEYS.criteria]: [filterDataKeys.providerType, filterDataKeys.name, filterDataKeys.npi, filterDataKeys.providerId],
};

export const getWorklistAdfFieldMap = (
	customAttributes: CustomAttributes.IAllResponse[],
): Partial<Record<keyof ClaimWorkLists.ISearchWorklistCriteria, string[]>> => {
	const customAttributeKeys = _.map(customAttributes, (attribute) => `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`);
	return {
		[CRITERIA_KEYS.criteria]: [
			filterDataKeys.id,
			filterDataKeys.name,
			filterDataKeys.type,
			filterDataKeys.priority,
			filterDataKeys.assignees,
		],
		...(!_.isEmpty(customAttributeKeys) ? { [CRITERIA_KEYS.customAttributeADF]: customAttributeKeys } : {}),
	};
};

export const getEncounterAdfFieldMap = (): Partial<Record<keyof Patients.IEncounterSearchCriteria, string[]>> => {
	return {
		[CRITERIA_KEYS.criteria]: [filterDataKeys.encounterNumber, filterDataKeys.encounterServiceDate, filterDataKeys.encounterId],
		[CRITERIA_KEYS.providerADF]: [filterDataKeys.providerId],
		[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountNumber, filterDataKeys.accountId],
	};
};

export const getGroupedChargesByEncounterAdfFieldMap = (): Partial<Record<keyof Accounts.IChargeItemSearch, string[]>> => {
	return {
		[CRITERIA_KEYS.encounterADF]: [filterDataKeys.encounterNumber, filterDataKeys.encounterServiceDate],
		[CRITERIA_KEYS.providerADF]: [filterDataKeys.providerId],
		[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountId],
	};
};

export const getChargeItemAdfFieldMap = (): Partial<Record<keyof Patients.IChargeItemSearchCriteria, string[]>> => {
	return {
		[CRITERIA_KEYS.criteria]: [filterDataKeys.encounterId],
		[CRITERIA_KEYS.encounterADF]: [filterDataKeys.encounterServiceDate],
		[CRITERIA_KEYS.providerADF]: [filterDataKeys.providerId],
		[CRITERIA_KEYS.accountADF]: [filterDataKeys.accountNumber, filterDataKeys.accountId],
	};
};

export const buildFieldToAdf = (adfMap: Partial<Record<CriteriaKey, string[]>>): Record<string, CriteriaKey> => {
	return _.transform(
		adfMap,
		(result: Record<string, CriteriaKey>, fields: string[], adf: CriteriaKey) => {
			fields?.forEach((field) => {
				result[field] = adf;
			});
		},
		{} as Record<string, CriteriaKey>,
	);
};
